import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useThemeContext } from '../../../../theme/ThemeContextProvider';
import { ICountry, IState } from 'country-state-city';
import {
  CityOption,
  getCitiesOfState,
  getCountries,
  getStatesOfCountry,
} from '../../../../utils/coutry-city-utils';

import { useGetIndustriesQuery } from '../../../../store/apiSlice/jobpost/jobpostApi';
import Input from '../../../../components/MUIComponents/Input';
import JobPostFilterMultiSelect from '../../../JobPostsPage/components/JobPostsSidebar/JobPostsFilters/JobPostFilterMultiSelect';
import ArrowDownIcon from '../../../../components/Icons/ArrowDownIcon';
import AutocompleteOption from '../../../../components/shared/AutocompleteOption/AutocompleteOption';

export interface RegistrationFormInputs {
  companyName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phone: string;
  contactEmail: string;
  website: string;
}
// TODO: somebody fix this shame
interface CompanyDetailsProps {
  setIndustryFilterState: any;
  setValue: any;
  watch: any;
  register: any;
  industryFilterState: any;
  errors: any;
  control: any;
}

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  setIndustryFilterState,
  setValue,
  watch,
  register,
  errors,
  industryFilterState,
  control,
}) => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedCountryCode, setSelectedCountryCode] = useState<
    string | undefined
  >();
  const [selectedStateCode, setSelectedStateCode] = useState<
    string | undefined
  >();
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [city, setCity] = useState<string>('');

  const { data: sportsIndustries } = useGetIndustriesQuery();

  const handleFilterChange = (industriesArray: string[]) => {
    setIndustryFilterState(industriesArray.join(','));
  };

  const findCountryByName = (
    name: string | null | undefined
  ): ICountry | null => {
    const countries = getCountries();
    return countries.find((country) => country.name === name) || null;
  };

  const findStateByName = (name: string | null | undefined): IState | null => {
    return states.find((state) => state.name === name) || null;
  };

  const previousCountryRef = useRef<string | null>(watch('country') || null);
  const previousStateRef = useRef<string | null>(watch('state') || null);

  useEffect(() => {
    const initialCountry = watch('country');
    const initialState = watch('state');
    const initialCity = watch('city');

    if (!initialCountry) {
      return;
    }

    const country = findCountryByName(initialCountry);
    const countryCode = country?.isoCode;

    if (!countryCode) {
      return;
    }

    setSelectedCountryCode(countryCode);
    const statesOfCountry = getStatesOfCountry(countryCode);
    setStates(statesOfCountry || []);

    if (initialState) {
      const preState = statesOfCountry?.find((st) => st.name === initialState);
      const stateCode = preState?.isoCode;
      setSelectedStateCode(stateCode || undefined);

      if (stateCode) {
        const citiesOfState = getCitiesOfState(countryCode, stateCode);
        setCities(citiesOfState || []);
      }
    }

    if (initialCity) {
      setCity(initialCity);
    }
  }, [watch]);

  useEffect(() => {
    const subscription = watch(
      (value: Record<'country' | 'state' | 'city', string>) => {
        const countryName = value.country || null;
        if (previousCountryRef.current !== countryName) {
          previousCountryRef.current = countryName;

          const country = findCountryByName(countryName);
          const countryCode = country?.isoCode;

          if (countryCode) {
            setSelectedCountryCode(countryCode);
            const statesOfCountry = getStatesOfCountry(countryCode);
            setStates(statesOfCountry || []);

            setSelectedStateCode(undefined);
            setCities([]);
            setCity('');
            setValue('state', '');
            setValue('city', '');

            previousStateRef.current = null;
          } else {
            setSelectedCountryCode(undefined);
            setStates([]);
            setCities([]);
            setValue('state', '');
            setValue('city', '');
            setSelectedStateCode(undefined);
            setCity('');

            previousStateRef.current = null;
          }

          return;
        }

        const stateName = value.state || null;
        if (previousStateRef.current !== stateName) {
          previousStateRef.current = stateName;

          if (!selectedCountryCode) {
            setSelectedStateCode(undefined);
            setCities([]);
            setCity('');
            setValue('city', '');
            return;
          }

          if (!stateName) {
            setSelectedStateCode(undefined);
            setCities([]);
            setCity('');
            setValue('city', '');
            return;
          }

          const preState = states.find((st) => st.name === stateName);
          const stateCode = preState?.isoCode;
          setSelectedStateCode(stateCode || undefined);
          setCities(
            stateCode
              ? getCitiesOfState(selectedCountryCode, stateCode) || []
              : []
          );
          setCity('');
          setValue('city', '');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [watch, setValue, selectedCountryCode, states]);

  useEffect(() => {
    if (selectedCountryCode && selectedStateCode) {
      const citiesOfState = getCitiesOfState(
        selectedCountryCode,
        selectedStateCode
      );
      setCities(citiesOfState || []);
      setValue('city', city || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode, selectedStateCode, setValue]); // Intentionally omitting city - only run when country/state changes
  return (
    <Box display="flex" flexDirection="column" alignSelf="center" width="100%">
      <Box display="flex" flexDirection="column" gap="36px">
        <Input
          containerFlex="1"
          caption="Company name:"
          variant="outlined"
          fullWidth
          placeholder="Enter Company name"
          register={register('companyName')}
          error={!!errors.companyName}
          helperText={errors.companyName ? errors.companyName.message : ''}
        />
        <Box flex={1}>
          <JobPostFilterMultiSelect
            label={'industry'}
            placeholder={'Choose one'}
            selectItems={sportsIndustries || []}
            onChange={handleFilterChange}
            value={
              industryFilterState.length ? industryFilterState.split(',') : []
            }
          />
        </Box>
      </Box>
      <Divider sx={{ m: '48px 0' }} />
      <Box display="flex" flexDirection="column" gap="36px">
        <Typography component="h3" variant="h3">
          Company contact
        </Typography>
        <Input
          containerFlex="1"
          caption="Address:"
          variant="outlined"
          fullWidth
          placeholder="Enter Address"
          register={register('address')}
          error={!!errors.address}
          helperText={errors.address ? errors.address.message : ''}
        />

        <Box
          display="flex"
          flexDirection={mobileView ? 'column' : 'row'}
          gap={mobileView ? '28px' : '22px'}
        >
          <Controller
            name="country"
            control={control}
            render={({ field }) => {
              const countryValue =
                typeof field.value === 'string'
                  ? findCountryByName(field.value)
                  : field.value;

              return (
                <FormControl
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  variant="outlined"
                  fullWidth
                  error={!!errors.country}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      padding: '0 2px',
                      mb: '12px',
                      color: field.disabled ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    Countries:
                  </Typography>
                  <Autocomplete
                    {...field}
                    options={getCountries()}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value?.name
                    }
                    value={countryValue || null}
                    onChange={(event, newValue) => {
                      const nextCountryCode = newValue?.isoCode;
                      const isCountryChanged =
                        nextCountryCode !== selectedCountryCode;

                      setSelectedCountryCode(nextCountryCode);
                      field.onChange(newValue ? newValue.name : null);

                      if (!newValue || isCountryChanged) {
                        setSelectedStateCode(undefined);
                        setValue('state', '');
                        setValue('city', '');
                        setValue('zipCode', '');
                        setStates(
                          newValue
                            ? getStatesOfCountry(nextCountryCode || '') || []
                            : []
                        );
                        setCities([]);
                        setCity('');

                        previousStateRef.current = null;
                      }
                    }}
                    renderOption={(props, option) => (
                      <AutocompleteOption text={option.name} props={props} />
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a country"
                        error={!!errors.country}
                        helperText={
                          errors.country ? errors.country.message : ''
                        }
                      />
                    )}
                    sx={{
                      '.MuiInputBase-root': {
                        height: '3.125rem',
                      },
                      '.MuiSvgIcon-root': {
                        bgcolor: 'transparent !important',
                      },
                    }}
                    popupIcon={<ArrowDownIcon />}
                  />
                </FormControl>
              );
            }}
          />

          <Controller
            name="state"
            control={control}
            render={({ field }) => {
              const stateValue =
                typeof field.value === 'string'
                  ? findStateByName(field.value)
                  : field.value;

              return (
                <FormControl
                  fullWidth
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  variant="outlined"
                  error={!!errors.state}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      padding: '0 2px',
                      mb: '12px',
                      color: selectedCountryCode
                        ? 'text.primary'
                        : 'text.disabled',
                    }}
                  >
                    State / Province:
                  </Typography>
                  <Autocomplete
                    {...field}
                    options={states}
                    getOptionLabel={(option) => option.name || ''}
                    value={stateValue || null}
                    onChange={(event, newValue) => {
                      const nextStateCode = newValue?.isoCode;
                      const isStateChanged =
                        nextStateCode !== selectedStateCode;

                      setSelectedStateCode(nextStateCode);
                      field.onChange(newValue ? newValue.name : null);

                      if (!newValue || isStateChanged) {
                        setValue('city', '');
                        setCity('');
                      }

                      if (!newValue) {
                        setCities([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a state"
                        error={!!errors.state}
                        helperText={errors.state ? errors.state.message : ''}
                        disabled={!selectedCountryCode}
                      />
                    )}
                    renderOption={(props, option) => (
                      <AutocompleteOption text={option.name} props={props} />
                    )}
                    disabled={!selectedCountryCode}
                    sx={{
                      '.MuiInputBase-root': {
                        height: '3.125rem',
                      },
                      '.MuiSvgIcon-root': {
                        bgcolor: 'transparent !important',
                      },
                    }}
                    popupIcon={<ArrowDownIcon />}
                  />
                </FormControl>
              );
            }}
          />
        </Box>

        <Box
          display="flex"
          width={'100%'}
          flexDirection={mobileView ? 'column' : 'row'}
          gap={mobileView ? '28px' : '22px'}
        >
          <Controller
            name="city"
            control={control}
            render={({ field }) => {
              const cityValue =
                typeof field.value === 'string' && field.value !== ''
                  ? { name: field.value, id: '' }
                  : field.value;
              return (
                <FormControl
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  variant="outlined"
                  fullWidth
                  error={!!errors.city}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      padding: '0 2px',
                      mb: '12px',
                      color: selectedStateCode
                        ? 'text.primary'
                        : 'text.disabled',
                    }}
                  >
                    City:
                  </Typography>
                  <Autocomplete
                    {...field}
                    options={selectedStateCode ? cities : []}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value?.name
                    }
                    value={cityValue || null}
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.name : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a city"
                        error={!!errors.city}
                        helperText={errors.city ? errors.city.message : ''}
                        disabled={!selectedStateCode}
                      />
                    )}
                    disabled={!selectedStateCode}
                    renderOption={(props, option) => (
                      <AutocompleteOption text={option.name} props={props} />
                    )}
                    sx={{
                      '.MuiInputBase-root': {
                        height: '3.125rem',
                      },
                      '.MuiSvgIcon-root': {
                        bgcolor: 'transparent !important',
                      },
                    }}
                    popupIcon={<ArrowDownIcon />}
                  />
                </FormControl>
              );
            }}
          />

          <Input
            containerFlex="1"
            caption="ZIP code:"
            variant="outlined"
            fullWidth
            placeholder="Enter ZIP code"
            register={register('zipCode')}
            error={!!errors.zipCode}
            helperText={errors.zipCode ? errors.zipCode.message : ''}
          />
        </Box>

        <Box
          display="flex"
          width={'100%'}
          flexDirection={mobileView ? 'column' : 'row'}
          gap={mobileView ? '28px' : '22px'}
        >
          <Input
            width={'100%'}
            containerFlex="1"
            caption="Phone:"
            variant="outlined"
            fullWidth
            placeholder="Enter Phone"
            register={register('phone')}
            error={!!errors.phone}
            helperText={errors.phone ? errors.phone.message : ''}
          />
          <Input
            width={'100%'}
            containerFlex="1"
            caption="Email:"
            variant="outlined"
            type={'email'}
            fullWidth
            placeholder="Enter Email"
            register={register('contactEmail')}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail ? errors.contactEmail.message : ''}
          />
        </Box>
        <Box display="flex">
          <Box flex={mobileView ? '1' : '0 1 calc(50% - 11px)'}>
            <Input
              containerFlex="1"
              caption="Website:"
              variant="outlined"
              fullWidth
              placeholder="Enter Website"
              register={register('website')}
              error={!!errors.website}
              helperText={errors.website ? errors.website.message : ''}
            />
            {errors.website && (
              <Typography
                variant={'body4'}
                color={theme.palette.text.disabled}
                sx={{
                  padding: '0 13px',
                }}
              >
                {`Please include https:// (e.g., https://www.yourcompany.com)`}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyDetails;
